import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import VerifyEmailPage from "./pages/VerifyEmailPage";
import CheckEmailPage from "./pages/CheckEmailPage";

// Manager Pages
import ManagerLayout from "./components/Layout/ManagerLayout";
import ManagerDashboard from "./pages/manager/Dashboard";
import PostShiftPage from "./pages/manager/PostShift";
import ApplicantsPage from "./pages/manager/Applicants";
import LogsPage from "./pages/manager/Logs";
import ShiftRecordsPage from "./pages/manager/ShiftRecords";
import ManagerMorePage from "./pages/manager/More";
import ManagerBranchesPage from "./pages/manager/Branches";
import StaffPage from "./pages/manager/Staff";
import AlertsPage from "./pages/manager/Alerts";
import ManagerMyShifts from "./pages/manager/MyShifts";
import ManagerProfile from "./pages/manager/Profile";
import ManagerAnalytics from "./pages/manager/Analytics";
import PenaltiesDashboard from "./pages/manager/PenaltiesDashboard";
import PaymentSuccess from "./pages/manager/PaymentSuccess";
import BillingPage from "./pages/manager/BillingPage";
import UsageStatementPage from "./pages/manager/UsageStatementPage";

// Worker Pages
import WorkerLayout from "./components/Layout/WorkerLayout";
import AvailableShiftsPage from "./pages/worker/AvailableShifts";
import MyShiftsPage from "./pages/worker/MyShifts";
import ShiftTrackerPage from "./pages/worker/ShiftTracker";
import TillChecksPage from "./pages/worker/TillChecks";
import WorkerProfileEdit from "./pages/worker/ProfileEdit";

// Shared Pages
import NotificationsPage from "./pages/shared/NotificationsPage";
import ChatPage from "./pages/shared/ChatPage";
import HelpLegalPage from "./pages/shared/HelpLegalPage";
import SettingsPage from "./pages/SettingsPage";

// Public Pages
import LandingPage from "./pages/LandingPage";
// Feb 2026 Marketing Redesign — new Jax-inspired marketing site
import MarketingLayout from "./components/marketing/MarketingLayout";
import HomePage from "./pages/marketing/HomePage";
import HowItWorksPage from "./pages/marketing/HowItWorksPage";
import FeaturesPage from "./pages/marketing/FeaturesPage";
import IndustriesPage from "./pages/marketing/IndustriesPage";
import PricingPage from "./pages/marketing/PricingPage";
// legacy: manager-scoped PricingPage kept at /manager/pricing route
import ManagerPricingPage from "./pages/manager/PricingPage";
import AboutPage from "./pages/marketing/AboutPage";
import TrustCentrePage from "./pages/marketing/TrustCentrePage";
import PrivacyPage from "./pages/PrivacyPage";
import TermsPage from "./pages/TermsPage";
import CookiesPage from "./pages/CookiesPage";
import PublicHelpLegalPage from "./pages/HelpLegalPage";
import LearnMorePage from "./pages/LearnMorePage";
import TrustCenterPage from "./pages/TrustCenterPage";
import StatusPage from "./pages/StatusPage";
import PenaltyPaymentPage from "./pages/PenaltyPaymentPage";
import PenaltyPaymentResultPage from "./pages/PenaltyPaymentResultPage";
import AccountabilityAgreementPage from "./pages/AccountabilityAgreementPage";
import AccountDeletionRequestPage from "./pages/AccountDeletionRequestPage";

// Onboarding
import ManagerOnboarding from "./components/Onboarding/ManagerOnboarding";
import WorkerOnboarding from "./components/Onboarding/WorkerOnboarding";
import PostOfficeProfileGate from "./components/Onboarding/PostOfficeProfileGate";
import LegalAcceptanceStep from "./components/Onboarding/LegalAcceptanceStep";
import InstallPwaStep from "./components/Onboarding/InstallPwaStep";
import TrialReadyStep from "./components/Onboarding/TrialReadyStep";
import EnableNotificationsStep from "./components/Onboarding/EnableNotificationsStep";
import AccountReadyStep, { READY_PENDING_KEY } from "./components/Onboarding/AccountReadyStep";

// Admin
import AdminDashboard from "./pages/AdminDashboard";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-slate-200 border-t-[#DA291C] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    const dest = user?.role === 'admin' ? '/admin' : user?.role === 'manager' ? '/manager' : '/worker';
    return <Navigate to={dest} replace />;
  }

  return children;
};

// Public Route
const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center">
        <div className="h-8 w-8 border-2 border-slate-200 border-t-[#DA291C] rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    const dest = user?.role === 'admin' ? '/admin' : user?.role === 'manager' ? '/manager' : '/worker';
    return <Navigate to={dest} replace />;
  }

  return children;
};

// Onboarding wrapper — Phase 3 UX (Feb 2026).
//
// Blocking sequence (bare minimum before the user reaches the product):
//   1. Profile setup   — ManagerOnboarding / WorkerOnboarding
//   2. Legal           — combined Terms + Privacy + Accountability
//
// Optional, skippable prompts (do NOT block entry):
//   3. Install to phone  — one-tap "Do this later" persists dismissal
//   4. Notification opt-in — one-tap "Not now" persists dismissal
//
// Finally, a one-time role-specific "you're ready" screen (persisted in
// localStorage so we don't re-show it on subsequent logins) hands the user
// off to Find Cover (manager) or Find Shifts (worker).
//
// Legacy/seeded users skip all gates. Existing users whose legal or
// install flags were already set on the server will also transparently
// skip those individual steps.
const OnboardingGate = ({ children, role }) => {
  const { user, refreshUser } = useAuth();
  if (!user) return children;

  const isSeededOrDemo = user.isSeeded === true || user.isDemo === true;

  // Step 1: Minimal profile setup.
  if (user.onboardingCompleted === false && !isSeededOrDemo) {
    if (role === 'manager') return <ManagerOnboarding />;
    if (role === 'worker') return <WorkerOnboarding />;
  }

  // Step 2: Combined legal step — Privacy + Terms + Accountability.
  // Individual accept endpoints are still called for audit-log fidelity.
  const needsPrivacy = !user.privacyAcceptedVersion;
  const needsTerms = !user.termsAcceptedVersion;
  const needsAccountability = user.accountabilityAgreementAccepted !== true;
  if ((needsPrivacy || needsTerms || needsAccountability) && !isSeededOrDemo) {
    return <LegalAcceptanceStep onDone={refreshUser} />;
  }

  // iter95 — Trial Ready confirmation. Only shown to managers who
  // haven't yet acknowledged their auto-granted 14-day trial. Removes
  // the need for any plan / payment prompt during onboarding.
  if (role === 'manager' && !isSeededOrDemo && user.trialReadyAcknowledged !== true) {
    return <TrialReadyStep role="manager" onDone={refreshUser} />;
  }

  // iter98 — Post Office-specific profile completion for EXISTING workers
  // who onboarded before this feature landed. Shows a lightweight one-off
  // gate that captures the two matching signals (currentRole +
  // yearsExperience). Once saved, `postOfficeProfileCompletedAt` is set
  // and the gate never fires again for that user.
  if (
    role === 'worker'
    && !isSeededOrDemo
    && user.onboardingCompleted === true
    && !(user.workerProfile && user.workerProfile.currentPostOfficeRole && user.workerProfile.yearsExperience)
  ) {
    return <PostOfficeProfileGate onDone={refreshUser} />;
  }

  // Step 3 (optional): install prompt — skippable.
  if (user.installPromptHandled !== true && !isSeededOrDemo) {
    return <InstallPwaStep onDone={refreshUser} />;
  }

  // Step 4 (optional): notifications — skippable.
  if (user.notificationPromptHandled !== true && !isSeededOrDemo) {
    return <EnableNotificationsStep onDone={refreshUser} />;
  }

  // Step 5: one-time "account ready" hand-off. Only shown to users who just
  // completed the setup step in the current session (or any session where
  // the pending flag is still set). Returning users who already went
  // through onboarding never see this screen again.
  if (!isSeededOrDemo && user.id) {
    let readyPending = false;
    try { readyPending = localStorage.getItem(READY_PENDING_KEY(user.id)) === '1'; } catch { /* storage blocked */ }
    if (readyPending) return <AccountReadyStep role={role} />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/check-email" element={<CheckEmailPage />} />
      
      {/* Manager Routes */}
      <Route 
        path="/manager" 
        element={
          <ProtectedRoute allowedRoles={['manager']}>
            <OnboardingGate role="manager">
              <ManagerLayout />
            </OnboardingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<ManagerDashboard />} />
        <Route path="post-shift" element={<PostShiftPage />} />
        <Route path="my-shifts" element={<ManagerMyShifts />} />
        <Route path="analytics" element={<ManagerAnalytics />} />
        <Route path="applicants" element={<ApplicantsPage />} />
        <Route path="logs" element={<LogsPage />} />
        <Route path="logs/:shiftId" element={<LogsPage />} />
        <Route path="shift-records" element={<ShiftRecordsPage />} />
        <Route path="shift-records/:shiftId" element={<LogsPage />} />
        <Route path="staff" element={<StaffPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="accountability" element={<PenaltiesDashboard />} />
        <Route path="pricing" element={<ManagerPricingPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="billing/usage" element={<UsageStatementPage />} />
        <Route path="payment-success" element={<PaymentSuccess />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="help" element={<HelpLegalPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<ManagerProfile />} />
        {/* Phase 1 UX consolidation — new grouped landing page */}
        <Route path="more" element={<ManagerMorePage />} />
        {/* Alias for saved branches (previously deep inside Profile) so
            the "More → Branches" link has a first-class URL. Uses the
            existing Profile page which already hosts the branches UI. */}
        <Route path="branches" element={<ManagerBranchesPage />} />
      </Route>

      {/* Worker Routes */}
      <Route 
        path="/worker" 
        element={
          <ProtectedRoute allowedRoles={['worker']}>
            <OnboardingGate role="worker">
              <WorkerLayout />
            </OnboardingGate>
          </ProtectedRoute>
        }
      >
        <Route index element={<AvailableShiftsPage />} />
        <Route path="my-shifts" element={<MyShiftsPage />} />
        <Route path="tracker" element={<ShiftTrackerPage />} />
        <Route path="till-checks" element={<TillChecksPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="help" element={<HelpLegalPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="profile" element={<WorkerProfileEdit />} />
      </Route>

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute allowedRoles={['admin']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* Public Landing Page */}
      <Route path="/" element={<MarketingLayout />}>
        <Route index element={<HomePage />} />
        <Route path="how-it-works" element={<HowItWorksPage />} />
        <Route path="features" element={<FeaturesPage />} />
        <Route path="industries" element={<IndustriesPage />} />
        <Route path="pricing" element={<PricingPage />} />
        <Route path="about" element={<AboutPage />} />
        <Route path="trust" element={<TrustCentrePage />} />
      </Route>
      {/* Legacy landing page kept accessible for anyone deep-linking old CTAs */}
      <Route path="/legacy-home" element={<LandingPage />} />

      {/* Public Legal Pages */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/help-legal" element={<PublicHelpLegalPage />} />
      <Route path="/learn-more" element={<LearnMorePage />} />
      <Route path="/trust" element={<TrustCenterPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/penalty-pay/result" element={<PenaltyPaymentResultPage />} />
      <Route path="/penalty-pay/:penaltyId" element={<PenaltyPaymentPage />} />
      <Route path="/accountability-agreement" element={<AccountabilityAgreementPage />} />
      <Route path="/account-deletion" element={<AccountDeletionRequestPage />} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
